import { uploadsActions, uploadsSelectors } from '@anm/store/modules/uploads';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { videoFolderActions, videoFolderSelectors } from '../../../../store/modules/videoFolder';

import useCreateProject from './useCreateProject';

type UseUploadProgressProps = {
  projectId: string;
  hostId?: string;
};

const useUploadProgress = ({ projectId, hostId = '' }: UseUploadProgressProps) => {
  const [showProgress, setShowProgress] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const dispatch = useDispatch();

  const { uploadId: createdUploadId, hostId: createdVideoHostId } =
    useSelector(uploadsSelectors.selectLatestUpload) || {};

  const mediaProgress = useSelector(uploadsSelectors.selectMediaProgress);
  const progress = createdUploadId ? mediaProgress?.[createdUploadId].uploadProgress : null;

  const createdProjectId = useSelector(videoFolderSelectors.selectCreatedProjectId);

  const isNewVideo = hostId === createdVideoHostId;
  const isProjectCreated = !!createdProjectId;

  const currentUploadProgress = showProgress ? progress || 0 : 100;

  const UPLOAD_MAX_PROGRESS = 95;

  const simulatedProgress =
    currentUploadProgress >= UPLOAD_MAX_PROGRESS && !isProjectCreated
      ? UPLOAD_MAX_PROGRESS
      : isProjectCreated
      ? 100
      : currentUploadProgress;

  useCreateProject({
    hostId,
    isNewVideo,
    uploadId: createdUploadId
  });

  useEffect(() => {
    isNewVideo && setShowProgress(true);
  }, [isNewVideo]);

  useEffect(() => {
    if (!isProjectCreated || !isNewVideo) return;

    setShowProgress(false);
    setShowSuccess(true);

    const clearSucessTimeout = setTimeout(() => {
      setShowSuccess(false);
      dispatch(videoFolderActions.updateCreatedProject({ id: projectId, projectId: createdProjectId }));
      dispatch(videoFolderActions.clearCreatedProject());
      dispatch(uploadsActions.clearUploadedFiles());
    }, 2000);

    return () => {
      clearTimeout(clearSucessTimeout);
    };
  }, [isProjectCreated, isNewVideo]);

  return [showProgress, showSuccess, simulatedProgress, isProjectCreated] as const;
};

export default useUploadProgress;
